import React, { useRef, useEffect, useState } from 'react';
import { Button, Box, Container, Paper, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
// import StripedGuide from "../../handys_screens/components/StripedGuide";

const CANVAS_WIDTH = 450;
const CANVAS_HEIGHT = 200;
const LINE_HEIGHT = 7;
const LINE_MARGIN = 3;
const LINE_COLOR = '#888';
const LINE_Y = CANVAS_HEIGHT - 10 - LINE_HEIGHT; // 10px above bottom

const BUS_WIDTH = 130;
const BUS_HEIGHT = 50;
const BUS_MARGIN_BOTTOM = 8; // Space above the line
const BUS_COLOR = '#007e0fff';
const WHEEL_RADIUS = 11;
const WHEEL_COLOR = '#222';

// Door and window dimensions
const doorWidth = 18;
const doorHeight = 38;
const doorColor = "#e0e0e0";
const windowWidth = 22;
const windowHeight = 25;
const windowColor = "#ffffffff";

const BUS_START_X = 20;

// Additional rectangle dimensions
const addRectWidth = 160;
const addRectHeight = 13;
const addRectMarginBottom = 13;
const addRectMarginLeft = 240;
const addRectX = addRectMarginLeft;
const addRectY = CANVAS_HEIGHT - addRectHeight - addRectMarginBottom;

export const Bus_Stop_Trainning = () => {
  const canvasRef = useRef(null);
  const [busX, setBusX] = useState(BUS_START_X);
  const [speed, setSpeed] = useState(0);
  const [result, setResult] = useState(null);
  const [timer, setTimer] = useState(0); // in seconds
  const [timerActive, setTimerActive] = useState(false);
  const timerStartRef = useRef(null);
  const navigate = useNavigate();

  // Timer effect with high precision
  useEffect(() => {
    let animationFrame;
    function updateTimer() {
      if (timerActive && timerStartRef.current) {
        const elapsed = (Date.now() - timerStartRef.current) / 1000;
        setTimer(elapsed);
        animationFrame = requestAnimationFrame(updateTimer);
      }
    }
    if (timerActive) {
      animationFrame = requestAnimationFrame(updateTimer);
    }
    return () => cancelAnimationFrame(animationFrame);
  }, [timerActive]);

  // Animation loop
  useEffect(() => {
    let animationFrame;
    function animate() {
      setBusX(prevX => {
        let nextX = prevX + speed;
        if (nextX < 0) nextX = 0;
        if (nextX > CANVAS_WIDTH - BUS_WIDTH) nextX = CANVAS_WIDTH - BUS_WIDTH;
        // Check if bus is at the right edge while moving
        if (nextX + BUS_WIDTH >= CANVAS_WIDTH - 1 && speed > 0) {
          setSpeed(0);
          setResult("fail_right");
          setTimerActive(false);
        }
        return nextX;
      });
      animationFrame = requestAnimationFrame(animate);
    }
    if (speed !== 0) {
      animationFrame = requestAnimationFrame(animate);
    }
    return () => cancelAnimationFrame(animationFrame);
  }, [speed]);

  // Draw bus and line
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw additional rectangle as a trapez (Bus Stop Platform)
    const trapezBottomWidth = 160;
    const trapezTopWidth = 140;
    const trapezHeight = 13;
    const trapezMarginBottom = 13;
    const trapezMarginLeft = 240;
    const trapezBottomX = trapezMarginLeft;
    const trapezBottomY = CANVAS_HEIGHT - trapezHeight - trapezMarginBottom;
    const trapezTopX = trapezBottomX + (trapezBottomWidth - trapezTopWidth) / 2;
    const trapezTopY = trapezBottomY;

    ctx.beginPath();
    // Bottom left
    ctx.moveTo(trapezBottomX, trapezBottomY + trapezHeight);
    // Bottom right
    ctx.lineTo(trapezBottomX + trapezBottomWidth, trapezBottomY + trapezHeight);
    // Top right
    ctx.lineTo(trapezTopX + trapezTopWidth, trapezTopY);
    // Top left
    ctx.lineTo(trapezTopX, trapezTopY);
    ctx.closePath();
    ctx.fillStyle = "#d0d0d0ff";
    ctx.fill();

    // Draw horizontal line
    ctx.fillStyle = LINE_COLOR;
    ctx.fillRect(
      LINE_MARGIN, // x
      LINE_Y,      // y
      CANVAS_WIDTH - 2 * LINE_MARGIN, // width
      LINE_HEIGHT  // height
    );

    // Draw right bottom rectangle (bus stop marker)
    const rectWidth = 4;
    const rectHeight = 80;
    const rectMarginLeft = 80;
    const rectMarginBottom = 25;
    const rectX = CANVAS_WIDTH - rectMarginLeft;
    const rectY = CANVAS_HEIGHT - rectHeight - rectMarginBottom;
    ctx.fillStyle = "#444";
    ctx.fillRect(rectX, rectY, rectWidth, rectHeight);

    // Draw circle on top of stop marker
    const circleRadius = 15;
    const circleCenterX = rectX + rectWidth - circleRadius;
    const circleCenterY = rectY - circleRadius / 2;

    ctx.beginPath();
    ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, Math.PI * 2);
    ctx.fillStyle = "#ffe600"; // yellow fill
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#00800fff"; // green border
    ctx.stroke();

    // Draw "H" in the circle
    ctx.font = "bold 22px Arial";
    ctx.fillStyle = "#222";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("H", circleCenterX, circleCenterY + 2);

    // Draw bus body
    const busY = LINE_Y - BUS_HEIGHT - BUS_MARGIN_BOTTOM;
    ctx.fillStyle = BUS_COLOR;
    ctx.fillRect(busX, busY, BUS_WIDTH, BUS_HEIGHT);

    // Draw bus elements: door, window, door, window, door
    let currentX = busX + 6;
    const doorY = LINE_Y - BUS_HEIGHT - BUS_MARGIN_BOTTOM + 6;
    const windowY = doorY;

    // First door
    ctx.fillStyle = doorColor;
    ctx.fillRect(currentX, doorY, doorWidth, doorHeight);
    currentX += doorWidth + 6;

    // First window
    ctx.fillStyle = windowColor;
    ctx.fillRect(currentX, windowY, windowWidth, windowHeight);
    currentX += windowWidth + 6;

    // Second door
    ctx.fillStyle = doorColor;
    ctx.fillRect(currentX, doorY, doorWidth, doorHeight);
    currentX += doorWidth + 6;

    // Second window
    ctx.fillStyle = windowColor;
    ctx.fillRect(currentX, windowY, windowWidth, windowHeight);
    currentX += doorWidth + 9;

    // Third door
    ctx.fillStyle = doorColor;
    ctx.fillRect(currentX, doorY, doorWidth, doorHeight);
    currentX += doorWidth + 6;

    // Draw wheels
    ctx.beginPath();
    ctx.arc(busX + 30, busY + BUS_HEIGHT + 0, WHEEL_RADIUS, 0, Math.PI * 2);
    ctx.arc(busX + BUS_WIDTH - 30, busY + BUS_HEIGHT + 0, WHEEL_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = WHEEL_COLOR;
    ctx.fill();

    // Draw bus front (right side, simple rounded corner)
    ctx.beginPath();
    ctx.moveTo(busX + BUS_WIDTH, busY);
    ctx.lineTo(busX + BUS_WIDTH, busY + BUS_HEIGHT);
    ctx.quadraticCurveTo(
      busX + BUS_WIDTH + 10, busY + BUS_HEIGHT,  
      busX + BUS_WIDTH + 8, busY + BUS_HEIGHT / 1.8,
    );
    ctx.closePath();
    ctx.fillStyle = BUS_COLOR;
    ctx.fill();

    // Draw bus lights (front right)
    ctx.beginPath();
    ctx.arc(busX + BUS_WIDTH + 5, busY + BUS_HEIGHT - 10, 4, 0, Math.PI * 2);
    ctx.fillStyle = "#ffd700";
    ctx.fill();
  }, [busX]);

  // Button handlers
  const handleRight = () => {
    setSpeed(prev => prev === 0 ? 1 : prev * 2);
    setResult(null);
    timerStartRef.current = Date.now();
    setTimer(0);
    setTimerActive(true);
  };

  const handleLeft = () => {
    setSpeed(prev => {
      let newSpeed;
      if (prev === 0) newSpeed = 0;
      else if (prev === 1) newSpeed = 0;
      else newSpeed = prev / 2;
      if (newSpeed === 0 && prev !== 0) {
        setTimerActive(false);
        const busFront = busX + BUS_WIDTH;
        const busBack = busX;
        const rectLeft = addRectX;
        const rectRight = addRectX + addRectWidth;
        // Rule 1: Bus is correctly stopped on platform
        if (busFront < rectRight && busBack > rectLeft) {
          setResult("success");
        }
        // Rule 2: Bus is completely on the right side (touching or at the right edge)
        else if (busFront >= CANVAS_WIDTH - 1) {
          setResult("fail_right");
        }
        // Otherwise: fail
        else {
          setResult("fail");
        }
      }
      return newSpeed;
    });
  };

  // Restart handler
  const handleRestart = () => {
    setBusX(BUS_START_X);
    setSpeed(0);
    setResult(null);
    setTimer(0);
    setTimerActive(false);
    timerStartRef.current = null;
  };

  return (
    <>

    <Container maxWidth="md" sx={{ py: 4 }}>
      
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/user_manuals')}
        sx={{ mb: 3 }}
      >
        Zurück zum Handbuch
      </Button>
      
<Paper elevation={10} sx={{ mt: 1, mb: 1, p: 0 }}>
            <Box
              sx={{
                width: "100%",
                height: "30px",
                background: 'repeating-linear-gradient(45deg, #000 0 35px, #FFD700 35px 70px)',
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: 22, lineHeight: 1 }}>
                nur Anleitung
              </Typography>
            </Box>
           
          </Paper>

      <Box sx={{ position: 'relative', width: CANVAS_WIDTH, margin: '0 auto' }}>
        <h2 style={{ textAlign: 'center' }}>Bus Stop Training Game</h2>
        <Box sx={{
          position: 'absolute',
          top: 52,
          left: 12,
          background: '#fff',
          borderRadius: 2,
          px: 2,
          py: 1,
          fontWeight: 'bold',
          fontSize: 18,
          color: '#1976d2',
          zIndex: 2,
          boxShadow: 1,
        }}>
          Zeit: {timer.toFixed(3)} s
        </Box>
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          style={{ border: '1px solid #ccc', background: '#fff' }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleLeft}
          disabled={result !== null}
        >
          Bremsen
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={handleRight}
          disabled={result !== null}
        >
          Start
        </Button>
      </Box>
      {result === "success" && (
        <div style={{ color: "green", fontWeight: "bold", fontSize: 32, margin: "16px 0" }}>
          SEHR GUT, keine Prämie.
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleRestart}
            >
              Versuch noch mal
            </Button>
          </Box>
        </div>
      )}
      {(result === "fail" || result === "fail_right") && (
        <div style={{ color: "red", fontWeight: "bold", fontSize: 32, margin: "16px 0" }}>
          Falsch, Abmahnung!!!
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleRestart}
            >
              Versuch noch mal
            </Button>
          </Box>
        </div>
      )}


      
      {/* <StripedGuide /> */}
    </Container>
    </>
  );
};

export default Bus_Stop_Trainning;